import { FormEvent, useState } from "react";

import { PageShell } from "../components/PageShell.js";
import { Seo } from "../components/Seo.js";

type Status = "idle" | "sending" | "sent" | "error";

export function RaiseConcern() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get("name") ?? ""),
      email: String(data.get("email") ?? ""),
      message: String(data.get("message") ?? ""),
      website: String(data.get("website") ?? ""), // honeypot
    };

    if (!payload.message.trim()) {
      setStatus("error");
      setError("Please describe your concern.");
      return;
    }

    setStatus("sending");
    setError("");
    try {
      const res = await fetch("/api/concern", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.error || "Something went wrong.");
      }
      form.reset();
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  return (
    <PageShell>
      <Seo
        title="Raise a Concern · JivaSeva"
        description="Have a concern about JivaSeva's website, communications, or volunteers? Let our volunteer team know and we'll look into it thoughtfully."
        path="/raise-a-concern"
      />
      <h1 className="page-heading">Raise a Concern</h1>
      <p className="manifesto">
        JivaSeva is built on trust, transparency, and mutual respect. If you have
        a concern about our website, communications, volunteers, or any aspect of
        JivaSeva, we encourage you to let us know.
      </p>
      {status === "sent" ? (
        <p className="saved-note" role="status">
          Thank you — your concern has been received. A human will read it within
          48 hours.
        </p>
      ) : (
        <form className="concern-form" onSubmit={handleSubmit} noValidate>
          <label className="field">
            <span>Your name (optional)</span>
            <input type="text" name="name" placeholder="Anonymous" />
          </label>
          <label className="field">
            <span>Email (so we can follow up)</span>
            <input type="email" name="email" placeholder="you@example.com" />
          </label>
          <label className="field">
            <span>What happened?</span>
            <textarea
              name="message"
              rows={5}
              placeholder="Share as much as you'd like…"
            />
          </label>
          {/* Honeypot: hidden from users, catches bots. */}
          <input
            type="text"
            name="website"
            className="hp-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          {status === "error" && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="primary-button"
            disabled={status === "sending"}
          >
            {status === "sending" ? "Submitting…" : "Submit concern"}
          </button>
        </form>
      )}
      <p className="manifesto">
        Every message is reviewed with care by our volunteer team. We will look
        into each concern thoughtfully and respond as appropriate.
      </p>
    </PageShell>
  );
}
